import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { supabase } from '../lib/supabase';
import logo from '../../src/assets/LogoNavbar.png';
import { useRecordPageView } from '../hooks/useRecordPageView';
import { captureSupabaseError } from '../lib/sentry';
import { tracking } from '../util/tracking';

interface InviteData {
  id: string;
  assessment_id: string;
  template_title: string;
  inviting_organisation_name: string;
  status: string;
  email: string;
}

export default function Invite() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useRecordPageView();

  // Auth form states
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        if (!inviteId) throw new Error('Invite not found');

        const { data, error } = await supabase
          .from('supplier_invites')
          .select(
            'id, assessment_id, template_title, inviting_organisation_name, status, email',
          )
          .eq('id', inviteId)
          .single();

        if (error || !data) throw error ?? new Error('Invite not found');
        setInvite(data as InviteData);
      } catch (err) {
        captureSupabaseError(err, { op: 'invite.fetch' });
        console.error(err);
        setError('Invite not found or expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [inviteId]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    if (!invite) {
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: invite.email,
      password,
    });

    if (error) setAuthError(error.message);
    else setAuthSuccess('Login successful! Redirecting...');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    tracking.log('UI', 'invite_register_submit', {
      page: 'invite',
      inviteId,
    });
    setAuthError('');
    setAuthSuccess('');
    setCreating(true);

    if (!invite) {
      return;
    }

    try {
      const res = await supabase.functions.invoke('signup-and-provision', {
        body: {
          email: invite.email,
          password,
          first_name: firstName,
          last_name: lastName,
          invite_id: inviteId,
          flow: 'supplier',
        },
      });

      const { error, response } = res;

      // If the function returned an HTTP error, extract the real message:
      if (error) {
        let message = 'An unexpected error occurred.';
        try {
          // Try parsing the response body (it could be JSON or text)
          const text = await response?.text();
          if (text) {
            try {
              const json = JSON.parse(text);
              message = json.error || json.message || text;
            } catch {
              message = text;
            }
          }
        } catch (err) {
          console.error('Error reading response body:', err);
        }

        // If this is an "email already registered" case, prompt user to sign in
        if (/already.*registered|already.*exists|email.*taken/i.test(message)) {
          setAuthError(message);
          //   setMode('login');
        } else {
          setAuthError(message);
        }

        console.error('Edge function error:', { message, error });
        return;
      }

      // If successful, show confirmation
      setAuthSuccess(
        'Registration successful! Please check your email to confirm your account.',
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'invite.register' });
      console.error('Unexpected error during register:', err);
      setAuthError(err.message || 'Unexpected error occurred.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Container className="text-center">
          <Alert variant="danger">{error}</Alert>
        </Container>
      </div>
    );
  }

  if (!invite) return null;

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center">
      <Container className="py-5">
        <Card className="shadow-lg rounded-3 overflow-hidden">
          <Row className="g-0">
            {/* Left column */}
            <Col
              md={6}
              className="bg-primary text-white p-4 d-flex flex-column justify-content-between"
              style={{ minHeight: '500px' }}
            >
              <div>
                <div className="d-flex align-items-center mb-4">
                  <img src={logo} alt="SupplyPassport Logo" height="70" />
                </div>

                <h1 className="h3 fw-bold mb-3">You've been invited!</h1>

                <p className="mb-4">
                  Your partner{' '}
                  <span className="fw-semibold text-brand">
                    {invite.inviting_organisation_name}
                  </span>{' '}
                  has invited you to complete the questionnaire:{' '}
                  <span className="fw-bold text-brand">
                    {invite.template_title}
                  </span>
                  .
                </p>
              </div>
            </Col>

            {/* Right column */}
            <Col md={6} className="bg-white p-4">
              <div className="mb-4">
                <h2 className="h4 fw-bold mb-1">Create an account</h2>
                <p className="text-muted mb-0">
                  Register to accept your invitation
                </p>
              </div>

              {authError && <Alert variant="danger">{authError}</Alert>}
              {authSuccess ? (
                <Alert variant="success">{authSuccess}</Alert>
              ) : (
                <Form onSubmit={handleLogin} className="mb-3">
                  <Form.Group className="mb-3" controlId="firstName">
                    <Form.Label className="small">First Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={creating}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="lastName">
                    <Form.Label className="small">Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={creating}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label className="small">Email address</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-envelope" />
                      </span>
                      <Form.Control
                        type="email"
                        className="border-start-0"
                        value={invite.email}
                        required
                        disabled
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="password">
                    <Form.Label className="small">Password</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-lock" />
                      </span>
                      <Form.Control
                        type="password"
                        placeholder="••••••••"
                        className="border-start-0"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={creating}
                      />
                    </div>
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 btn-primary my-3"
                    disabled={creating}
                    onClick={handleRegister}
                  >
                    {creating ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Registering...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </Form>
              )}

              <div className="text-center small mt-3">
                Already have an account?{' '}
                <Link to={'/login'}>
                  <Button
                    variant="link"
                    className="p-0 fw-semibold text-decoration-none"
                  >
                    Sign in here
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </Card>

        <div className="text-center mt-3 small text-muted">
          By continuing, you agree to our{' '}
          <Link to="/terms-or-service">Terms of Service</Link> and{' '}
          <a href="/privacy-policy">Privacy Policy</a>.
        </div>
      </Container>
    </div>
  );
}
